import type { ConditionalDisplayGroup, FormField, ResponseInForm, Section } from "./formTypeStructure";

export function shouldDisplayFormField(field: FormField, allResponseInForms: Record<string, ResponseInForm>): boolean {
  return evaluateConditionalDisplay(allResponseInForms, field.conditionalDisplay);
}

export function shouldDisplaySection(section: Section, allResponseInForms: Record<string, ResponseInForm>): boolean {
  return evaluateConditionalDisplay(allResponseInForms, section.conditionalDisplay);
}

function evaluateConditionalDisplay(
    allResponseInForms: Record<string, ResponseInForm>,
    display?: ConditionalDisplayGroup | ConditionalDisplayGroup[],
): boolean {
  if (!display) return true;

  const groups = Array.isArray(display) ? display : [display];
  return groups.every(group => evaluateConditionalDisplayGroup(group, allResponseInForms));
}

function evaluateConditionalDisplayGroup(
  group: ConditionalDisplayGroup,
  allResponseInForms: Record<string, ResponseInForm>
): boolean {
  const evaluations = group.rules.map(rule => {
    const { fieldName, formFieldId, value, operator, numeric_compare_to, typeComputation } = rule;
    
    // Récupération des valeurs à tester
    const keys: string[] = Array.isArray(fieldName)
      ? fieldName
      : fieldName
        ? [fieldName]
        : [];

    // Fallback sur formFieldId s'il est présent
    const fieldKeys = keys.length === 0 && formFieldId
      ? Array.isArray(formFieldId)
        ? formFieldId
        : [formFieldId]
      : keys;

    // ✅ Typage explicite du tableau values avec le type ResponseInForm
    const values: ResponseInForm['responseValue'][] = fieldKeys
      .map(k => allResponseInForms[k]?.responseValue)
      .filter(v => v !== undefined);

    let finalValue: ResponseInForm['responseValue'] = values[0];

    if (typeComputation === "SOMME") {
      finalValue = values
        .map(v => typeof v === "number" ? v : 0)
        .reduce((acc, curr) => acc + curr, 0);
    } else if (typeComputation === "MULTIPLICATION") {
      finalValue = values
        .map(v => typeof v === "number" ? v : 1)
        .reduce((acc, curr) => acc * curr, 1);
    }

    switch (operator) {
      case "==":
        return finalValue === value;
      case "!=":
        return finalValue !== value;
      case ">":
        return typeof finalValue === "number" && finalValue > Number(value ?? numeric_compare_to);
      case "<":
        return typeof finalValue === "number" && finalValue < Number(value ?? numeric_compare_to);
      case "IN":
        return Array.isArray(value) && (value as any[]).includes(finalValue);
      case "NOT IN":
        return Array.isArray(value) && !(value as any[]).includes(finalValue);
      case "CONTAINS":
        return Array.isArray(finalValue) && (finalValue as any[]).includes(value);
      case "NOT CONTAINS":
        return Array.isArray(finalValue) && !(finalValue as any[]).includes(value);
      default:
        return false;
    }
  });

  return group.logic === "AND"
    ? evaluations.every(Boolean)
    : evaluations.some(Boolean);
}